import { ButtonV2, IconButton, Typography } from "@suraasa/placebo-ui"
import { Assignment, LearningOutcome } from "api/resources/assignments/types"
import { NavArrowLeft, NavArrowRight } from "iconoir-react"
import { useEffect, useRef, useState } from "react"

import { buildAssignmentTitle } from "../utils"
import AttachmentCard from "./AttachmentCard"
import AttachmentsDialog from "./AttachmentsDialog"
import styles from "./Description.module.css"
import DetailsSheet, { DetailsTabId } from "./DetailsSheet"

const TaskDescription = ({
  assignment,
  courseId,
  learningOutcomesContent,
}: {
  assignment: Assignment
  courseId: number
  learningOutcomesContent?: LearningOutcome[]
}) => {
  const [activeTab, setActiveTab] = useState<DetailsTabId | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)
  const [showAttachments, setShowAttachments] = useState(false)
  const attachments = assignment.attachments

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const update = () => {
      setShowLeft(el.scrollLeft > 0)
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }

    update()
    el.addEventListener("scroll", update)
    window.addEventListener("resize", update)

    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [])

  // This is done to support legacy format of assignment descriptions where newlines were not wrapped in <p> tags.
  const formattedAssignmentDescription = (assignment.description ?? "").replace(
    /\r\n|\r|\n/g,
    "<br />"
  )

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <Typography variant="title3" className="text-onSurface-800">
          Task Description
        </Typography>
      </div>

      <div className="mb-1.5 mt-3 bg-surface sm:mb-2 sm:mt-1.5 sm:rounded-2xl sm:border sm:border-onSurface-200 sm:p-2">
        <Typography variant="strong" className="mb-0.5">
          {buildAssignmentTitle(assignment)}
        </Typography>
        <div
          className={styles.Description}
          dangerouslySetInnerHTML={{
            __html: formattedAssignmentDescription,
          }}
        />
        <div className="relative mb-6 mt-2 sm:mb-0">
          <div className="relative">
            {showLeft && (
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center">
                <div className="h-full w-5 bg-gradient-to-r from-surface/70 to-transparent" />
              </div>
            )}

            {showRight && (
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center">
                <div className="h-full w-5 bg-gradient-to-l from-surface/70 to-transparent" />
              </div>
            )}
            <div
              ref={scrollRef}
              className="scrollbar-hide flex items-center gap-1 overflow-x-auto whitespace-nowrap"
            >
              {assignment.submissionGuidelines && (
                <ButtonV2
                  size="sm"
                  variant="primary"
                  className="px-1 py-[7px]"
                  onClick={() => setActiveTab("guidelines")}
                >
                  Submission Guidelines
                </ButtonV2>
              )}

              <ButtonV2
                size="sm"
                variant="primary"
                className="px-1 py-[7px]"
                onClick={() => setActiveTab("markingCriteria")}
              >
                Marking Criteria
              </ButtonV2>

              {assignment.instructions && (
                <ButtonV2
                  size="sm"
                  variant="primary"
                  className="px-1 py-[7px]"
                  onClick={() => setActiveTab("instructions")}
                >
                  Task Instructions
                </ButtonV2>
              )}

              <ButtonV2
                size="sm"
                variant="secondary"
                className="px-1 py-[7px]"
                onClick={() => setActiveTab("learningOutcomes")}
              >
                Learning Outcomes
              </ButtonV2>

              <ButtonV2
                size="sm"
                variant="secondary"
                className="border px-1 py-[7px]"
                onClick={() => setActiveTab("faqs")}
              >
                FAQs
              </ButtonV2>
            </div>
            {(showLeft || showRight) && (
              <div className="mr-1 mt-2 flex flex-row items-center justify-end gap-2">
                <IconButton
                  onClick={() =>
                    scrollRef.current?.scrollBy({
                      left: -150,
                      behavior: "smooth",
                    })
                  }
                >
                  <NavArrowLeft className="text-onSurface-900" />
                </IconButton>
                <IconButton
                  onClick={() =>
                    scrollRef.current?.scrollBy({
                      left: 150,
                      behavior: "smooth",
                    })
                  }
                >
                  <NavArrowRight className="text-onSurface-900" />
                </IconButton>
              </div>
            )}
          </div>

          <AttachmentCard
            attachments={attachments}
            onViewAll={() => setShowAttachments(true)}
          />
        </div>
      </div>

      {/* <div className="block sm:hidden">
        <div className=" mb-6 flex flex-col rounded-2xl border border-onSurface-300 bg-surface-100">
          <Button
            onClick={() => setViewMarkingCriteria(true)}
            variant={"text"}
            className="py-1.75"
          >
            View Marking Criteria
          </Button>
          <Divider className="bg-surface-300" />
          <Button
            onClick={() => setViewLearningOutcomes(true)}
            variant={"text"}
            className="py-1.75"
          >
            View Learning Outcomes
          </Button>
        </div>
      </div> */}

      <AttachmentsDialog
        open={showAttachments}
        onClose={() => setShowAttachments(false)}
        attachments={attachments}
      />

      <DetailsSheet
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        onClose={() => setActiveTab(null)}
        assignment={assignment}
        courseId={courseId}
        learningOutcomesContent={learningOutcomesContent}
      />
    </div>
  )
}

export default TaskDescription
